import { offset, shift } from '@floating-ui/react-dom';
import { DreamBlock } from '@typedream/core';
import { toDOMNode } from '@udecode/plate-core';
import classNames from 'classnames';
import Image from 'next/legacy/image';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Floating } from '../../../components/Floating';
import { Portal } from '../../../components/Portal';
import { getTElementPlugin, useTPlateEditorRef } from '../../../plate';
import { useDreamEditorStore } from '../../store';
import { selectOption } from '../transforms';
import styles from './SlashCommandMenu.module.css';

interface SlashCommandMenuProps {
  element: DreamBlock
}

export function SlashCommandMenu(props: SlashCommandMenuProps) {
  const { element } = props;

  const editor = useTPlateEditorRef();
  const domNode = toDOMNode(editor, element);

  const elementTypes = useDreamEditorStore((state) => state.slashCommandOptions);

  const options = useMemo(
    () => elementTypes
      .filter((type) => getTElementPlugin(editor, type).options?.meta)
      .map((type) => ({
        type,
        ...getTElementPlugin(editor, type).options?.meta,
      }))
      .filter(Boolean),
    [editor, elementTypes],
  );

  const [selected, setSelected] = useState(elementTypes[0]);

  useEffect(() => {
    if (!selected || !elementTypes.includes(selected)) {
      setSelected(elementTypes[0]);
    }
  }, [selected, elementTypes]);

  const confirmSelected = useCallback(() => {
    if (selected) {
      selectOption(editor, selected);
    }
  }, [editor, selected]);

  const close = () => {
    editor.store.setState({ isSlashCommandOpen: false });
  };

  useEffect(() => {
    if (!domNode) return () => { /*  */ };

    function handler(event: KeyboardEvent) {
      if (['ArrowDown', 'Down'].includes(event.key)) {
        const index = options.findIndex((o) => o.type === selected);
        setSelected(options[(index + 1) % options.length]?.type);
      } else if (['ArrowUp', 'Up'].includes(event.key)) {
        const index = options.findIndex((o) => o.type === selected);
        let nextIndex = (index - 1) % options.length;
        if (nextIndex < 0) nextIndex = options.length + nextIndex;
        setSelected(options[nextIndex]?.type);
      } else if (['Tab', 'Enter'].includes(event.key)) {
        confirmSelected();
      }
    }

    const rootNode = domNode.ownerDocument;

    rootNode.addEventListener('keydown', handler);

    return () => {
      rootNode.removeEventListener('keydown', handler);
    };
  }, [confirmSelected, domNode, options, selected]);

  if (!domNode) return null;

  return (
    // Mount on host document body.
    // This a way to make sure that the overlay background
    // fully covers the entire viewport
    <Portal container={globalThis?.document?.body}>
      <div
        role="none"
        className={styles.portal}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <Floating
          reference={domNode}
          options={{
            placement: 'bottom-start',
            middleware: [
              offset(8),
              shift({
                mainAxis: true,
                crossAxis: true,
                padding: {
                  top: 5,
                  bottom: 5,
                },
              }),
            ],
          }}
          autoUpdate
          className={styles.container}
        >
          <div className={styles.title}>Commonly Used</div>
          {options.map((opt) => (
            <div
              ref={(el) => {
                if (el && el.parentElement && opt.type === selected) {
                  const elTop = el.offsetTop;
                  const elBottom = el.offsetTop + el.offsetHeight;
                  const viewTop = el.parentElement.scrollTop;
                  const viewBottom = el.parentElement.scrollTop + el.parentElement.offsetHeight;
                  // when not in view, scroll into view
                  if (elTop < viewTop) {
                    if (elTop - el.offsetHeight >= viewTop) {
                      el.parentElement.scrollBy(0, -1 * el.offsetHeight);
                    } else {
                      el.scrollIntoView();
                    }
                  } else if (elBottom > viewBottom) {
                    if (elTop <= viewBottom) {
                      el.parentElement.scrollBy(0, el.offsetHeight);
                    } else {
                      el.scrollIntoView();
                    }
                  }
                }
              }}
              key={opt.type}
              className={classNames(styles.itemContainer, {
                [styles.selected]: opt.type === selected,
              })}
              onMouseMove={() => setSelected(opt.type)}
              onMouseDown={() => confirmSelected()}
              role="none"
            >
              <div className={styles.img}>
                <Image
                  src={opt.img || ''}
                  alt={opt.title}
                  width={25}
                  height={25}
                />
              </div>
              <div
                className={classNames(
                  styles.textContainer,
                  'is-flex is-flex-direction-row is-align-items-center',
                )}
              >
                <div className={classNames(styles.itemTitle)}>{opt.title}</div>
                {opt.tag && <div className="tag is-primary ml-2">{opt.tag}</div>}
              </div>
            </div>
          ))}
        </Floating>
      </div>
    </Portal>
  );
}
